import React from "react";
import Contact from "../contact";
import arrowIcon from "../../images/home/arrow-icon.svg";
import benifitIcon from "../../images/individual/benifitIcon.png";
import { FaCompass, FaCalendarAlt, FaExternalLinkAlt, FaRocket, FaCogs, FaUsers, FaMapMarkerAlt, FaChartLine, FaGlobe, FaCheckCircle, FaInfoCircle, FaBriefcase, FaGraduationCap, FaBullseye, FaHandshake } from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

const ImmigrationStrategyVisionFuture = () => {
  return (
    <div>
      <Helmet>
        <title>Immigration Strategy: A Vision for the Future - Immiget Global</title>
        <meta
          name="description"
          content="Explore Australia's Migration Strategy released December 2023. Over 25 policy commitments for streamlined visa processes, enhanced labor market matching, and regional workforce needs."
        />
        <meta 
          name="keywords" 
          content="australia migration strategy 2023, streamlined visa processes, labor market matching, regional workforce, migration policy reform" 
        />
      </Helmet>
      
      <div className="w-full h-90 font-Hellix bg-cover bg-center bg-image">
        <div className="max-w-[1440px] px-4 md:px-[75px] m-auto h-full">
          <div className="flex flex-col gap-2 md:gap-2 pt-10 md:pt-20 pb-[90px] h-full justify-center">
            <div className="text-white hellix-font text-center font-semibold text-[22px] md:text-[54px]">
              Immigration Strategy
            </div>
            <div className="text-white hellix-font text-center font-medium text-[16px] md:text-[24px] mb-4">
              A Vision for Australia's Future
            </div>
            <div className="flex justify-center gap-2 md:gap-4">
              <div className="text-white hellix-font text-[10px] lg:text-sm flex items-center md:text-lg font-medium">
                <Link to="/" className="hover:underline">HOME</Link>
              </div>
              <div className="flex items-center">
                <img src={arrowIcon} className="w-[5.8px] md:w-auto" alt="" />
                <img src={arrowIcon} className="w-[5.8px] md:w-auto" alt="" />
              </div>
              <div className="text-white hellix-font text-[10px] lg:text-sm flex items-center md:text-lg font-medium">
                <Link to="/news" className="hover:underline">NEWS</Link>
              </div>
              <div className="flex items-center">
                <img src={arrowIcon} className="w-[5.8px] md:w-auto" alt="" />
                <img src={arrowIcon} className="w-[5.8px] md:w-auto" alt="" />
              </div>
              <div className="text-white hellix-font text-[10px] lg:text-sm flex items-center md:text-lg font-medium uppercase">
                Immigration Strategy
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Article Header */}
      <div className="container max-w-1440px mx-auto px-4 py-6 xl:py-10 lg:px-14">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue text-white p-3 rounded-full">
              <FaCompass className="text-xl" />
            </div>
            <div>
              <div className="flex items-center gap-2 text-blue text-sm hellix-font font-medium">
                <FaCalendarAlt className="text-xs" />
                <span>December 2023</span>
              </div>
              <div className="text-blue text-sm hellix-font">Strategic Framework</div>
            </div>
          </div>
          
          <h1 className="xl:text-[42px] text-[32px] text-blue font-bold hellix-font mb-6 leading-tight">
            Immigration Strategy: A Vision for Australia's Future
          </h1>
          
          <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-6 mb-8">
            <div className="flex items-start gap-4">
              <FaInfoCircle className="text-blue text-xl mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Comprehensive Reform</h3>
                <p className="text-blue hellix-font text-lg leading-relaxed">
                  The Australian Government's Migration Strategy, released in <strong>December 2023</strong>, outlines a vision for a more <strong>streamlined and responsive migration system</strong>. The strategy includes <strong>over 25 policy commitments</strong> aimed at transforming Australia's immigration framework.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container max-w-1440px mx-auto px-4 lg:px-14">
        <div className="max-w-4xl mx-auto">
          
          {/* Overview Section */}
          <section className="mb-12">
            <div className="flex gap-2 mb-6">
              <div className="flex items-center">
                <img src={benifitIcon} alt="benefits" />
              </div>
              <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font">
                Strategic Vision Overview
              </h2>
            </div>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-blue hellix-font text-lg leading-relaxed mb-6">
                Australia's Migration Strategy represents the most comprehensive review of the immigration system in decades. This forward-looking strategy addresses the challenges of a rapidly changing global economy while ensuring Australia remains competitive in attracting the world's best talent.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 my-8">
                <div className="bg-white rounded-lg shadow-custom-white p-6 border-l-4 border-blue">
                  <div className="flex items-center gap-3 mb-4">
                    <FaBullseye className="text-blue text-2xl" />
                    <h3 className="text-blue font-semibold hellix-font text-lg">25+</h3>
                  </div>
                  <p className="text-3xl font-bold text-blue hellix-font">Policy</p>
                  <p className="text-blue hellix-font text-sm">Commitments for reform</p>
                </div>
                
                <div className="bg-white rounded-lg shadow-custom-white p-6 border-l-4 border-green-500">
                  <div className="flex items-center gap-3 mb-4">
                    <FaCogs className="text-green-600 text-2xl" />
                    <h3 className="text-blue font-semibold hellix-font text-lg">Streamlined</h3>
                  </div>
                  <p className="text-3xl font-bold text-green-600 hellix-font">System</p>
                  <p className="text-blue hellix-font text-sm">Simplified visa processes</p>
                </div>
                
                <div className="bg-white rounded-lg shadow-custom-white p-6 border-l-4 border-orange-500">
                  <div className="flex items-center gap-3 mb-4">
                    <FaRocket className="text-orange-600 text-2xl" />
                    <h3 className="text-blue font-semibold hellix-font text-lg">Future</h3>
                  </div>
                  <p className="text-3xl font-bold text-orange-600 hellix-font">Ready</p>
                  <p className="text-blue hellix-font text-sm">Responsive to economic needs</p>
                </div>
              </div>
            </div>
          </section>

          {/* Core Pillars */}
          <section className="mb-12">
            <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font mb-6 flex items-center gap-2">
              <FaBullseye className="text-blue" />
              Strategic Pillars
            </h2>
            
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-blue-50 to-white rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <FaCogs className="text-blue text-xl mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Simplifying Visa Processes</h3>
                    <p className="text-blue hellix-font leading-relaxed mb-3">
                      Streamlining complex visa pathways to create clearer, more efficient routes to permanent residency and reducing administrative burden for both applicants and employers.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2">
                        <FaCheckCircle className="text-green-500 text-sm flex-shrink-0" />
                        <span className="text-blue hellix-font text-sm">Reduced processing times</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <FaCheckCircle className="text-green-500 text-sm flex-shrink-0" />
                        <span className="text-blue hellix-font text-sm">Digital-first approach</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <FaCheckCircle className="text-green-500 text-sm flex-shrink-0" />
                        <span className="text-blue hellix-font text-sm">Clearer eligibility criteria</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-green-50 to-white rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <FaHandshake className="text-green-600 text-xl mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Enhanced Labor Market Matching</h3>
                    <p className="text-blue hellix-font leading-relaxed mb-3">
                      Improving the alignment between skilled migrants and Australia's economic needs through better data analysis and forecasting of skills demand.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2">
                        <FaCheckCircle className="text-green-500 text-sm flex-shrink-0" />
                        <span className="text-blue hellix-font text-sm">Real-time skills demand analysis</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <FaCheckCircle className="text-green-500 text-sm flex-shrink-0" />
                        <span className="text-blue hellix-font text-sm">Industry collaboration</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <FaCheckCircle className="text-green-500 text-sm flex-shrink-0" />
                        <span className="text-blue hellix-font text-sm">Predictive workforce planning</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-orange-50 to-white rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <FaMapMarkerAlt className="text-orange-600 text-xl mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Addressing Regional Workforce Needs</h3>
                    <p className="text-blue hellix-font leading-relaxed mb-3">
                      Targeted strategies to support population growth and economic development in regional areas through specialized visa pathways and incentives.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2">
                        <FaCheckCircle className="text-green-500 text-sm flex-shrink-0" />
                        <span className="text-blue hellix-font text-sm">Regional priority processing</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <FaCheckCircle className="text-green-500 text-sm flex-shrink-0" />
                        <span className="text-blue hellix-font text-sm">Dedicated regional pathways</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <FaCheckCircle className="text-green-500 text-sm flex-shrink-0" />
                        <span className="text-blue hellix-font text-sm">Community integration support</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Key Policy Commitments */}
          <section className="mb-12">
            <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font mb-6 flex items-center gap-2">
              <FaChartLine className="text-blue" />
              Key Policy Commitments
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-custom-white p-6">
                  <h3 className="text-blue font-semibold hellix-font text-lg mb-4 flex items-center gap-2">
                    <FaBriefcase className="text-blue" />
                    Skills & Employment
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <FaCheckCircle className="text-green-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-blue hellix-font text-sm">Streamlined skills assessment processes</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <FaCheckCircle className="text-green-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-blue hellix-font text-sm">Enhanced employer nomination schemes</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <FaCheckCircle className="text-green-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-blue hellix-font text-sm">Faster temporary to permanent transitions</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <FaCheckCircle className="text-green-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-blue hellix-font text-sm">Industry-specific migration pathways</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-custom-white p-6">
                  <h3 className="text-blue font-semibold hellix-font text-lg mb-4 flex items-center gap-2">
                    <FaGraduationCap className="text-blue" />
                    International Education
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <FaCheckCircle className="text-green-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-blue hellix-font text-sm">Improved student visa processing</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <FaCheckCircle className="text-green-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-blue hellix-font text-sm">Post-study work entitlements review</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <FaCheckCircle className="text-green-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-blue hellix-font text-sm">Quality education provider focus</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <FaCheckCircle className="text-green-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-blue hellix-font text-sm">Graduate retention strategies</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Innovation & Technology */}
          <section className="mb-12">
            <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font mb-6">
              Innovation & Technology Focus
            </h2>
            
            <div className="grid gap-6">
              <div className="bg-gradient-to-r from-blue-50 to-white rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <FaRocket className="text-blue text-xl mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-blue font-semibold hellix-font text-lg mb-3">Digital Transformation</h3>
                    <p className="text-blue hellix-font mb-4">
                      The strategy emphasizes leveraging technology to create a more efficient, user-friendly immigration system:
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2">
                          <FaCogs className="text-blue text-sm flex-shrink-0" />
                          <span className="text-blue hellix-font text-sm">Automated decision-making systems</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <FaCogs className="text-blue text-sm flex-shrink-0" />
                          <span className="text-blue hellix-font text-sm">AI-powered case assessment</span>
                        </li>
                      </ul>
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2">
                          <FaCogs className="text-blue text-sm flex-shrink-0" />
                          <span className="text-blue hellix-font text-sm">Digital identity verification</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <FaCogs className="text-blue text-sm flex-shrink-0" />
                          <span className="text-blue hellix-font text-sm">Real-time application tracking</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-white rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <FaChartLine className="text-green-600 text-xl mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-blue font-semibold hellix-font text-lg mb-3">Data-Driven Decision Making</h3>
                    <p className="text-blue hellix-font mb-4">
                      Using advanced analytics to inform migration policies and ensure they meet Australia's economic objectives:
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2">
                          <FaChartLine className="text-green-500 text-sm flex-shrink-0" />
                          <span className="text-blue hellix-font text-sm">Labor market intelligence</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <FaChartLine className="text-green-500 text-sm flex-shrink-0" />
                          <span className="text-blue hellix-font text-sm">Skills demand forecasting</span>
                        </li>
                      </ul>
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2">
                          <FaChartLine className="text-green-500 text-sm flex-shrink-0" />
                          <span className="text-blue hellix-font text-sm">Economic impact assessment</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <FaChartLine className="text-green-500 text-sm flex-shrink-0" />
                          <span className="text-blue hellix-font text-sm">Migration outcome monitoring</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Implementation Timeline */}
          <section className="mb-12">
            <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font mb-6">
              Implementation Roadmap
            </h2>
            
            <div className="bg-white rounded-lg shadow-custom-white p-6">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-blue text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">1</div>
                  <div>
                    <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Short-term (2024-2025)</h3>
                    <p className="text-blue hellix-font">Implementation of immediate reforms including streamlined processing and digital enhancements</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-green-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">2</div>
                  <div>
                    <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Medium-term (2025-2027)</h3>
                    <p className="text-blue hellix-font">Roll-out of major system reforms and enhanced labor market matching capabilities</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-orange-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">3</div>
                  <div>
                    <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Long-term (2027+)</h3>
                    <p className="text-blue hellix-font">Full integration of all reforms and continuous improvement based on performance data</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Expected Outcomes */}
          <section className="mb-12">
            <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font mb-6 flex items-center gap-2">
              <FaGlobe className="text-blue" />
              Expected Outcomes
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow-custom-white p-6 text-center">
                <FaUsers className="text-blue text-3xl mx-auto mb-3" />
                <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Better Talent Attraction</h3>
                <p className="text-blue hellix-font text-sm">Attracting and retaining the world's best talent to drive innovation and economic growth</p>
              </div>
              
              <div className="bg-white rounded-lg shadow-custom-white p-6 text-center">
                <FaCogs className="text-blue text-3xl mx-auto mb-3" />
                <h3 className="text-blue font-semibold hellix-font text-lg mb-2">System Efficiency</h3>
                <p className="text-blue hellix-font text-sm">Faster, more transparent processes that reduce uncertainty for applicants and employers</p>
              </div>
              
              <div className="bg-white rounded-lg shadow-custom-white p-6 text-center">
                <FaChartLine className="text-blue text-3xl mx-auto mb-3" />
                <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Economic Growth</h3>
                <p className="text-blue hellix-font text-sm">Enhanced contribution of migration to Australia's economic prosperity and competitiveness</p>
              </div>
            </div>
          </section>


          {/* Official Resources */}
          <section className="mb-12">
            <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font mb-6">
              Official Resources & Documentation
            </h2>
            
            <div className="bg-blue-50 rounded-lg p-6">
              <p className="text-blue hellix-font mb-4">
                For the complete Migration Strategy document and detailed implementation plans:
              </p>
              <a
                href="https://immi.homeaffairs.gov.au/what-we-do/migration-strategy"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-blue font-medium hellix-font hover:text-blue-700 transition-colors"
              >
                <FaExternalLinkAlt className="text-sm" />
                Department of Home Affairs - Australian Migration Strategy
              </a>
            </div>
          </section>

          {/* Related Articles */}
          <section className="mb-12">
            <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font mb-6">
              Related Immigration News
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Link to="/news/migration-program-2024-25" className="bg-white rounded-lg shadow-custom-white p-6 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center gap-3 mb-3">
                  <FaChartLine className="text-blue text-lg" />
                  <span className="text-blue text-sm hellix-font font-medium">Current Program</span>
                </div>
                <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Migration Program 2024–25: Focus on Skills and Regional Australia</h3>
                <p className="text-blue hellix-font text-sm">Current implementation of strategic priorities outlined in the Migration Strategy...</p>
              </Link>
              
              <Link to="/news" className="bg-white rounded-lg shadow-custom-white p-6 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center gap-3 mb-3">
                  <FaGlobe className="text-blue text-lg" />
                  <span className="text-blue text-sm hellix-font font-medium">All News</span>
                </div>
                <h3 className="text-blue font-semibold hellix-font text-lg mb-2">View All Immigration News</h3>
                <p className="text-blue hellix-font text-sm">Stay informed about all developments in Australian immigration policy...</p>
              </Link>
            </div>
          </section>
        </div>
      </div>

      {/* Contact Section */}
      <Contact source="Immigration Strategy Vision Future Blog" />
    </div>
  );
};

export default ImmigrationStrategyVisionFuture;